import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { editTaskPlan, type ITaskPlanEdit } from '@apis/schedule';
import { scheduleKey } from '@constants/queryKeys';
import type { IRootState } from '@store/slices';
import type { ITaskPlanFields } from '@store/slices/scheduler.slice';
import type { ISchedulerData, ISchedulerTask, SiteId, TaskId } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';
import { useOwnPlanWriteAck } from './useOwnPlanWriteAck';
import { useSchedulerActions } from './useSchedulerStore';

// ---------------------------------------------------------------------------
// useTaskPlanAutoSave — the drawer's plan fields, saved as they are edited
// (2026-09-23 amendment to the task-commit UX design).
//
// Profession, areas and crew size belong to one task and move no other, so
// they skip the toolbar Save and its review: the store updates at once and the
// value rides the per-task immediate-write channel (debounced, coalesced,
// serialized, retried) to `PUT …/tasks/:taskId/plan`.
//
// That write bumps the schedule version, like any plan write, and
// `useOwnPlanWriteAck` acknowledges it as ours (or rebases when another editor
// wrote in between).
//
// A task the server doesn't have yet (added since the last Save) can't take a
// per-task write: its fields travel with that Save, and `autoSaves` says so.
// ---------------------------------------------------------------------------

/** The saved-baseline copy of a task with `edit` applied — mirrors the reducers. */
function applyEdit(task: ISchedulerTask, edit: ITaskPlanEdit): ISchedulerTask {
  const next = { ...task };
  if (edit.profession === null) delete next.profession;
  else if (edit.profession !== undefined) next.profession = edit.profession;
  if (edit.workerCount === null) delete next.workerCount;
  else if (edit.workerCount !== undefined) next.workerCount = edit.workerCount;
  if (edit.areaIds !== undefined) {
    const kept = new Map((task.locations ?? []).map((location) => [location.locationId, location]));
    next.locations = edit.areaIds.map((locationId) => kept.get(locationId) ?? { locationId, status: 'unplanned' });
  }
  return next;
}

/**
 * What the drawer's Save changed of the auto-saved fields besides areas, or
 * null when neither did. A profession is compared by id (the snapshot's other
 * columns are display copies); an absent value is sent as `null`, clearing it.
 */
export function changedPlanFields(saved: ISchedulerTask, draft: ISchedulerTask): ITaskPlanFields | null {
  const fields: ITaskPlanFields = {};
  if (draft.profession?.professionId !== saved.profession?.professionId) fields.profession = draft.profession ?? null;
  if (draft.workerCount !== saved.workerCount) fields.workerCount = draft.workerCount ?? null;
  return Object.keys(fields).length > 0 ? fields : null;
}

export interface ITaskPlanAutoSave {
  /** Whether this task's edits save themselves (false: they ride the toolbar Save). */
  autoSaves: () => boolean;
  /** Queue an edit already applied to the store. */
  save: (edit: ITaskPlanEdit) => void;
  /**
   * The drawer's Save: apply the profession / crew the draft changed to the
   * store and queue them. Call BEFORE `updateTask`, so the plan update that
   * follows finds them already in place and marks nothing dirty for them.
   */
  saveFields: (saved: ISchedulerTask, draft: ISchedulerTask) => void;
}

export function useTaskPlanAutoSave(siteId: SiteId, scheduleNumber: string | undefined, taskId: TaskId): ITaskPlanAutoSave {
  const qc = useQueryClient();
  const store = useStore<IRootState>();
  const { taskPlanFieldsSet } = useSchedulerActions();
  const ackOwnWrite = useOwnPlanWriteAck(siteId);
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);

  const autoSaves = useCallback(
    () =>
      scheduleNumber !== undefined &&
      (qc.getQueryData<ISchedulerData>(scheduleKey(siteId, scheduleNumber))?.tasks.some((task) => task.id === taskId) ?? false),
    [qc, siteId, scheduleNumber, taskId],
  );

  const save = useCallback(
    (edit: ITaskPlanEdit) => {
      // No history reset: an undo entry holds only the fields its own edit set,
      // and leaves a value saved since in place (QA F-45).
      write<ITaskPlanEdit>(
        ['plan'],
        edit,
        (value, ctx) =>
          ackOwnWrite(ctx, () => editTaskPlan(ctx.siteId, ctx.scheduleNumber, ctx.taskId, value), (task) => applyEdit(task, value)),
        // Two fields edited inside one debounce window go out as one request.
        { coalesce: (prev, next) => ({ ...prev, ...next }) },
      );
    },
    [write, ackOwnWrite],
  );

  const saveFields = useCallback(
    (saved: ISchedulerTask, draft: ISchedulerTask) => {
      const fields = changedPlanFields(saved, draft);
      if (!fields || !autoSaves() || store.getState().scheduler.staleBySite[siteId]) return;
      taskPlanFieldsSet({ siteId, taskId, fields });
      save(fields);
    },
    [autoSaves, save, store, siteId, taskId, taskPlanFieldsSet],
  );

  return useMemo(() => ({ autoSaves, save, saveFields }), [autoSaves, save, saveFields]);
}
